import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Percent } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VatRatesManagement = () => {
    const { token } = useAuth();
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRate, setEditingRate] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [rateToDelete, setRateToDelete] = useState(null);
    const [formData, setFormData] = useState({ name: '', rate: '', active: true });

    useEffect(() => { fetchRates(); }, []);

    const fetchRates = async () => {
        try {
            const response = await axios.get(`${API}/admin/vat-rates`, { headers: { Authorization: `Bearer ${token}` } });
            setRates(response.data);
        } catch { toast.error('Failed to load VAT rates'); }
        finally { setLoading(false); }
    };

    const handleOpenDialog = (rate = null) => {
        if (rate) {
            setEditingRate(rate);
            setFormData({ name: rate.name, rate: rate.rate.toString(), active: rate.active });
        } else {
            setEditingRate(null);
            setFormData({ name: '', rate: '', active: true });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name || formData.rate === '') {
            toast.error('Please fill in all fields');
            return;
        }
        setSaving(true);
        try {
            const payload = { name: formData.name, rate: parseFloat(formData.rate), active: formData.active };
            if (editingRate) {
                await axios.put(`${API}/admin/vat-rates/${editingRate.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('VAT rate updated');
            } else {
                await axios.post(`${API}/admin/vat-rates`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('VAT rate created');
            }
            setDialogOpen(false);
            fetchRates();
        } catch { toast.error('Failed to save VAT rate'); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!rateToDelete) return;
        setSaving(true);
        try {
            await axios.delete(`${API}/admin/vat-rates/${rateToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
            toast.success('VAT rate deleted');
            setDeleteConfirmOpen(false);
            setRateToDelete(null);
            fetchRates();
        } catch { toast.error('Failed to delete VAT rate'); }
        finally { setSaving(false); }
    };

    if (loading) {
        return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-brand-lime" /></div>;
    }

    return (
        <div className="max-w-3xl mx-auto animate-fade-in space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading text-brand-charcoal">VAT Rates</h1>
                    <p className="text-brand-gray-500 mt-1">Manage VAT rates applied to spares packs</p>
                </div>
                <Button onClick={() => handleOpenDialog()} className="bg-[#7AC943] hover:bg-[#68B035] text-black font-bold h-11" data-testid="add-vat-rate-btn">
                    <Plus className="h-5 w-5 mr-2" /> Add Rate
                </Button>
            </div>

            <Card className="border-0 shadow-lg">
                <CardContent className="pt-6">
                    {rates.length === 0 ? (
                        <div className="text-center py-12">
                            <Percent className="h-12 w-12 mx-auto text-brand-gray-300 mb-3" />
                            <p className="text-brand-gray-500">No VAT rates configured</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {rates.map(rate => (
                                <div key={rate.id} className="flex items-center justify-between p-4 bg-brand-gray-50 rounded-lg" data-testid={`vat-rate-${rate.id}`}>
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 bg-brand-lime/20 flex items-center justify-center">
                                            <span className="text-lg font-bold text-brand-charcoal">{rate.rate}%</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-brand-charcoal">{rate.name}</p>
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${rate.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {rate.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenDialog(rate)} data-testid={`edit-vat-rate-${rate.id}`}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { setRateToDelete(rate); setDeleteConfirmOpen(true); }} data-testid={`delete-vat-rate-${rate.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{editingRate ? 'Edit VAT Rate' : 'Add VAT Rate'}</DialogTitle>
                        <DialogDescription>{editingRate ? 'Update the VAT rate details' : 'Create a new VAT rate'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Name</Label>
                            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Standard, Reduced, Zero" className="h-11" data-testid="vat-name-input" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wide">Rate (%)</Label>
                            <Input type="number" step="0.1" min="0" value={formData.rate} onChange={(e) => setFormData({ ...formData, rate: e.target.value })} placeholder="20.0" className="h-11" data-testid="vat-rate-input" />
                        </div>
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Active</Label>
                            <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} data-testid="vat-active-switch" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} className="bg-[#7AC943] hover:bg-[#68B035] text-black" disabled={saving} data-testid="save-vat-rate-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete VAT Rate</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{rateToDelete?.name} ({rateToDelete?.rate}%)"? Packs using this rate will need to be reassigned.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving} data-testid="confirm-delete-vat-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default VatRatesManagement;
